import { trigger } from 'redial';
import { getRoutesProps } from '../routing';

function triggerAll(hooks, hookComponents, hookLocals) {
  const checkAborted = () => {
    const reason = hookLocals.aborted();
    if (reason && reason !== 'app-route-changed') {
      throw new Error(`Hooks have been terminated because of ${reason}`);
    }
    if (reason === 'app-route-changed') {
      console.log('[triggerHooks] app-route-changed before previous hooks were resolved'); // eslint-disable-line no-console
      return false;
    }
    return true;
  };

  return hooks.reduce((promise, parallelHooks) =>
    promise.then(() => {
      if (checkAborted()) {
        return Promise.all(
          [].concat(parallelHooks)
            .map((hook) => trigger(hook, hookComponents, hookLocals))
        );
      }
      return Promise.reject();
    }), Promise.resolve())
    .then(checkAborted);
}

function withTimeout(mainPromise, timeout) {
  let waitTimeout;
  const waitPromise = new Promise((resolve) => {
    waitTimeout = setTimeout(resolve, timeout);
  }).then(() => {
    throw new Error('Hooks have been terminated because of timeout');
  });

  return Promise.race([
    mainPromise,
    waitPromise,
  ]).then(() => {
    clearTimeout(waitTimeout);
  }).catch(() => {
    clearTimeout(waitTimeout);
  });
}

export default function triggerHooks({
  hooks,
  locals,
  renderProps,
  abort = () => undefined,
  aborted = () => false,
  timeout,
}) {
  // early exit if there are actually no hooks to trigger
  if (!hooks || hooks.length === 0) return Promise.resolve();

  const hookComponents = renderProps.components;
  const hookLocals = {
    location: renderProps.location,
    params: renderProps.params,
    routeProps: getRoutesProps(renderProps.routes),
    abort,
    aborted,
    ...locals,
  };

  const triggerPromise = triggerAll(hooks, hookComponents, hookLocals);

  return timeout
    ? withTimeout(triggerPromise, timeout)
    : triggerPromise;
}
